import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./EntryDataPointBrowser.css";
import { fetchFilters, fetchRows } from "../../services/entryDataPointBrowserService";

const TEXT_COLS = new Set([
  "date", "time", "index", "opt", "entry_code", "entry_type", "open_type",
  "exit_class", "mp_trend",
  "is_leg", "is_roll", "forced",
  "opv_rule", "vd_rule", "theta_rule", "oi_rule", "index_rule", "iv_rule",
  "opv_rule_0", "vd_rule_0", "theta_rule_0",
  "strong_trend", "strong_sell", "major_trend", "sideways", "sw_open",
  "opv_bs_neg", "opv_bs_pos", "th_bs_neg", "th_bs_pos", "vd_bs_neg", "vd_bs_pos",
]);

const MultiSelect = ({ label, values, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    const handler = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const toggle = v => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange([...next]);
  };
  const selSet = new Set(selected);
  const filtered = values.filter(v => String(v).toLowerCase().includes(search.toLowerCase()));
  const btn = selected.length === 0 ? "Any" : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  return (
    <div className="edpb-f" ref={rootRef}>
      <label>{label}</label>
      <div className="edpb-ms">
        <button className="edpb-ms-btn" type="button" onClick={e => { e.stopPropagation(); setOpen(!open); }}>{btn}</button>
        <div className={`edpb-ms-pop${open ? " edpb-open" : ""}`} onClick={e => e.stopPropagation()}>
          <input className="edpb-ms-search" placeholder="search…" value={search} onChange={e => setSearch(e.target.value)} />
          <div className="edpb-ms-tools">
            <button type="button" className="edpb-ms-link" onClick={() => onChange([...values])}>select all</button>
            <button type="button" className="edpb-ms-link" onClick={() => onChange([])}>clear</button>
          </div>
          <div>
            {filtered.map(v => (
              <label key={v} className="edpb-ms-row">
                <input type="checkbox" checked={selSet.has(v)} onChange={() => toggle(v)} />
                {v}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const fmtCell = v => {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return <span className={v ? "edpb-bool-t" : "edpb-bool-f"}>{String(v)}</span>;
  if (typeof v === "number") {
    const s = Number.isInteger(v) ? v : (Math.abs(v) >= 1000 ? v.toFixed(0) : v.toFixed(3));
    return <span className={v < 0 ? "edpb-neg" : v > 0 ? "edpb-pos" : ""}>{s}</span>;
  }
  return String(v);
};

const compareVals = (a, b) => {
  const aNull = a === null || a === undefined || a === "";
  const bNull = b === null || b === undefined || b === "";
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return (a === b) ? 0 : (a ? 1 : -1);
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

/**
 * Per-column filter expression evaluator.
 *  - numbers : >X, >=X, <X, <=X, =X, !=X, X..Y (range), plain X (exact); else substring on string repr
 *  - booleans: t/true/y/yes/1 → true; f/false/n/no/0 → false; else substring
 *  - strings : substring (case-insensitive); leading "!" negates
 *  - null    : matches "", "-", or "null"
 */
const passFilter = (value, expr) => {
  if (!expr) return true;
  const e = expr.trim();
  if (!e) return true;

  if (typeof value === "number") {
    let m = e.match(/^(>=|<=|>|<|=|!=)\s*(-?\d+(?:\.\d+)?)$/);
    if (m) {
      const n = parseFloat(m[2]);
      switch (m[1]) {
        case ">":  return value > n;
        case ">=": return value >= n;
        case "<":  return value < n;
        case "<=": return value <= n;
        case "=":  return value === n;
        case "!=": return value !== n;
        default:   return true;
      }
    }
    m = e.match(/^(-?\d+(?:\.\d+)?)\s*\.\.\s*(-?\d+(?:\.\d+)?)$/);
    if (m) {
      const lo = parseFloat(m[1]), hi = parseFloat(m[2]);
      return value >= lo && value <= hi;
    }
    if (/^-?\d+(?:\.\d+)?$/.test(e)) return value === parseFloat(e);
    return String(value).toLowerCase().includes(e.toLowerCase());
  }
  if (typeof value === "boolean") {
    const lo = e.toLowerCase();
    if (["t", "true", "y", "yes", "1"].includes(lo)) return value === true;
    if (["f", "false", "n", "no", "0"].includes(lo)) return value === false;
    return String(value).toLowerCase().includes(lo);
  }
  if (value === null || value === undefined) {
    const lo = e.toLowerCase();
    return lo === "-" || lo === "null" || lo === "";
  }
  if (e.startsWith("!")) {
    const sub = e.slice(1).toLowerCase();
    return sub === "" ? true : !String(value).toLowerCase().includes(sub);
  }
  return String(value).toLowerCase().includes(e.toLowerCase());
};

const EntryDataPointBrowser = () => {
  const [columns, setColumns] = useState([]);
  const [colOrder, setColOrder] = useState([]);
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  const dragFromRef = useRef(null);
  const dragMovedRef = useRef(false);
  const [dropTargetIdx, setDropTargetIdx] = useState(null);
  const [dropSide, setDropSide] = useState("before");

  const [optsEntry, setOptsEntry] = useState([]);
  const [optsIndex, setOptsIndex] = useState([]);
  const [optsOpt, setOptsOpt] = useState([]);
  const [optsEntryType, setOptsEntryType] = useState([]);

  const [entryCodeStr, setEntryCodeStr] = useState("");
  const [selIndex, setSelIndex] = useState([]);
  const [selOpt, setSelOpt] = useState([]);
  const [selEntryType, setSelEntryType] = useState([]);
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [limit, setLimit] = useState(500);

  const [colFilters, setColFilters] = useState({});

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [returned, setReturned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const entryCodeList = entryCodeStr.trim()
        ? entryCodeStr.split(/[,\s]+/).map(s => s.trim()).filter(Boolean)
        : [];
      const d = await fetchRows({
        entry_code: entryCodeList,
        index_name: selIndex,
        option_type: selOpt,
        entry_type: selEntryType,
        date_from: dfrom || null,
        date_to:   dto   || null,
        limit: Number(limit) || 500,
      });
      setRows(d.rows || []);
      setTotal(d.total || 0);
      setReturned(d.returned || 0);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [entryCodeStr, selIndex, selOpt, selEntryType, dfrom, dto, limit]);

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchFilters();
        setColumns(d.columns || []);
        setOptsEntry(d.entry_code || []);
        setOptsIndex(d.index_name || []);
        setOptsOpt(d.option_type || []);
        setOptsEntryType(d.entry_type || []);
      } catch (e) {
        setErr(e.message || String(e));
      }
    })();
  }, []);

  useEffect(() => {
    setColOrder(columns);
  }, [columns]);

  useEffect(() => {
    if (columns.length > 0) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.length]);

  const clearAll = () => {
    setEntryCodeStr(""); setSelIndex([]); setSelOpt([]); setSelEntryType([]);
    setDfrom(""); setDto(""); setLimit(500);
  };

  const filteredRows = useMemo(() => {
    const active = Object.entries(colFilters).filter(([, v]) => v && String(v).trim());
    if (active.length === 0) return rows;
    return rows.filter(r => active.every(([k, v]) => passFilter(r[k], v)));
  }, [rows, colFilters]);

  const { profitN, lossN } = useMemo(() => ({
    profitN: filteredRows.filter(r => typeof r.profit === "number" && r.profit > 0).length,
    lossN:   filteredRows.filter(r => typeof r.profit === "number" && r.profit < 0).length,
  }), [filteredRows]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const copy = [...filteredRows];
    copy.sort((ra, rb) => {
      const cmp = compareVals(ra[sortKey], rb[sortKey]);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filteredRows, sortKey, sortDir]);

  const handleSort = (c) => {
    if (dragMovedRef.current) return;
    if (sortKey === c) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(c);
      setSortDir(TEXT_COLS.has(c) ? "asc" : "desc");
    }
  };

  const onDragStart = (e, i) => {
    dragFromRef.current = i;
    dragMovedRef.current = false;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) {}
  };

  const onDragOver = (e, i) => {
    if (dragFromRef.current === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    dragMovedRef.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    const side = (e.clientX - rect.left) < rect.width / 2 ? "before" : "after";
    if (dropTargetIdx !== i || dropSide !== side) {
      setDropTargetIdx(i);
      setDropSide(side);
    }
  };

  const onDrop = (e, i) => {
    e.preventDefault();
    const from = dragFromRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const side = (e.clientX - rect.left) < rect.width / 2 ? "before" : "after";
    setDropTargetIdx(null);
    setDropSide("before");
    dragFromRef.current = null;
    if (from === null) return;
    let to = i + (side === "after" ? 1 : 0);
    if (to > from) to -= 1;
    if (to === from) return;
    const next = [...colOrder];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setColOrder(next);
  };

  const onDragEnd = () => {
    dragFromRef.current = null;
    setDropTargetIdx(null);
    setTimeout(() => { dragMovedRef.current = false; }, 0);
  };

  const resetView = () => {
    setColOrder(columns);
    setSortKey("date");
    setSortDir("desc");
    setColFilters({});
  };

  const setColFilter = (col, val) => {
    setColFilters(prev => {
      const next = { ...prev };
      if (val === "" || val == null) delete next[col]; else next[col] = val;
      return next;
    });
  };

  const clearColFilters = () => setColFilters({});
  const activeFilterCount = Object.values(colFilters).filter(v => v && String(v).trim()).length;

  return (
    <div className="edpb-root">
      <div className="edpb-filters">
        <div className="edpb-f">
          <label>Entry code</label>
          <input
            type="text"
            list="edpb-entry-code-opts"
            placeholder="e.g. CE_GAP_UP"
            value={entryCodeStr}
            onChange={e => setEntryCodeStr(e.target.value)}
          />
          <datalist id="edpb-entry-code-opts">
            {optsEntry.map(v => <option key={v} value={v} />)}
          </datalist>
        </div>
        <MultiSelect label="Index / Symbol" values={optsIndex}     selected={selIndex}     onChange={setSelIndex} />
        <MultiSelect label="Option type"    values={optsOpt}       selected={selOpt}       onChange={setSelOpt}   />
        <MultiSelect label="Entry type"     values={optsEntryType} selected={selEntryType} onChange={setSelEntryType} />
        <div className="edpb-f"><label>Date from</label><input type="date" value={dfrom} onChange={e => setDfrom(e.target.value)} /></div>
        <div className="edpb-f"><label>Date to</label><input type="date" value={dto} onChange={e => setDto(e.target.value)} /></div>
        <div className="edpb-f"><label>Limit</label><input type="number" min="1" max="10000" value={limit} onChange={e => setLimit(e.target.value)} /></div>
        <div className="edpb-f"><label>&nbsp;</label><button className="edpb-btn" onClick={load}>Apply</button></div>
        <div className="edpb-f"><label>&nbsp;</label><button className="edpb-btn sec" onClick={clearAll}>Clear</button></div>
      </div>

      <div className="edpb-meta">
        {err
          ? <span className="edpb-err">Error: {err}</span>
          : loading
            ? <span className="edpb-loading">Loading…</span>
            : <>
                <span className="edpb-pill">{sortedRows.length}/{returned} · {total} total</span>
                <span>profit: <b className="edpb-good-n">{profitN}</b></span>
                <span>loss: <b className="edpb-bad-n">{lossN}</b></span>
                {activeFilterCount > 0 && (
                  <span className="edpb-filt-active">
                    {activeFilterCount} column filter{activeFilterCount > 1 ? "s" : ""} active
                    <button className="edpb-clear-link" onClick={clearColFilters}>clear</button>
                  </span>
                )}
                <span className="edpb-hint">click header to sort · drag header to reorder · per-column filters: <code>&gt;10</code> <code>5..50</code> <code>!substr</code></span>
              </>}
        <button className="edpb-reset-link" onClick={resetView} title="Reset column order, sort, and column filters">Reset view</button>
      </div>

      <div className="edpb-wrap">
        <table className="edpb-table">
          <thead>
            <tr>
              {colOrder.map((c, i) => {
                const isFirst = i === 0;
                const isTxt = TEXT_COLS.has(c);
                const sorted = sortKey === c;
                const isDropTarget = dropTargetIdx === i;
                const cls = [
                  isFirst ? "edpb-first" : "",
                  isTxt ? "edpb-txt" : "",
                  sorted ? "edpb-sorted" : "",
                  isDropTarget ? `edpb-drop-${dropSide}` : "",
                ].filter(Boolean).join(" ");
                return (
                  <th
                    key={c}
                    draggable
                    onDragStart={(e) => onDragStart(e, i)}
                    onDragOver={(e) => onDragOver(e, i)}
                    onDrop={(e) => onDrop(e, i)}
                    onDragEnd={onDragEnd}
                    onClick={() => handleSort(c)}
                    title={`${c} — click to sort · drag to reorder`}
                    className={cls}
                  >
                    <span className="edpb-th-inner">
                      <span className="edpb-th-label">{c}</span>
                      <span className="edpb-sort-ind">{sorted ? (sortDir === "asc" ? "▲" : "▼") : "⇵"}</span>
                    </span>
                  </th>
                );
              })}
            </tr>
            <tr className="edpb-filt-row">
              {colOrder.map((c, i) => {
                const isFirst = i === 0;
                const isTxt = TEXT_COLS.has(c);
                const cls = [
                  "edpb-filt-cell",
                  isFirst ? "edpb-first" : "",
                  isTxt ? "edpb-txt" : "",
                ].filter(Boolean).join(" ");
                const v = colFilters[c] || "";
                return (
                  <th key={c} className={cls} onClick={e => e.stopPropagation()}>
                    <input
                      className={`edpb-filt-input${v ? " edpb-filt-on" : ""}`}
                      placeholder="filter…"
                      value={v}
                      onChange={e => setColFilter(c, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onMouseDown={e => e.stopPropagation()}
                      onDragStart={e => e.preventDefault()}
                      draggable={false}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r, ri) => {
              const cls = typeof r.profit === "number"
                ? (r.profit > 0 ? "edpb-good" : r.profit < 0 ? "edpb-wrong" : "")
                : "";
              return (
                <tr key={ri} className={cls}>
                  {colOrder.map((c, i) => (
                    <td key={c} className={`${i === 0 ? "edpb-first " : ""}${TEXT_COLS.has(c) ? "edpb-txt" : ""}`}>{fmtCell(r[c])}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EntryDataPointBrowser;
